import React from 'react';
import dynamic from 'next/dynamic';
import {
  BasicPopup,
  Box,
  HStack,
  Icongraphy,
  MovePagination,
  NewBasicButton,
  NewFlag,
  NewTextButton,
  Profile,
  Text,
  Tooltip,
  VStack,
} from '@design-system/pc';
import { ContentsSettingDataResponse } from '@display-component/shared';
import _ from 'lodash';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Agent } from '@remodeling/shop';
import ResponsiveBox from '@/components/ResponsiveBox/ResponsiveBox';
import DoubleQuotesStart from '@/components/svg/DoubleQuotesStart';
import ExpertSlideItem from './ExpertSlideItem';
import useExpertProfile from './hooks/useExpertProfile';
import TrackingService from '@/services/tracking';
import { HEADER_Z_INDEX } from '@/styles';

interface ExpertProfileProps {
  data: Agent;
  isBestRD: boolean;
  handleRequestConsult: () => void;
  profileAreaRef: any;
  constructionCaseData: ContentsSettingDataResponse[] | [];
}

const ExpertConstructionCase = dynamic(
  () => import('./ExpertConstructionCase'),
);

const ExpertProfile = ({
  data,
  isBestRD,
  handleRequestConsult,
  profileAreaRef,
  constructionCaseData,
}: ExpertProfileProps) => {
  const {
    scrapList,
    handleMutateScrapList,
    isOpenConstructionCase,
    handleOpenConstructionCase,
    handleCloseConstructionCase,
    setSwiper,
    isHover,
    setIsHover,
    handleClickNext,
    handleClickPrev,
  } = useExpertProfile({ data });

  return (
    <Box {...TrackingService.tagGTM('expert_detail_profile')}>
      <ResponsiveBox>
        <Box ref={profileAreaRef}>
          <HStack mt={48} space="space-between" alignment="start">
            <HStack space={24} alignment="start">
              <Box width="max-content" position="relative">
                <Profile
                  size="xlg"
                  src={data.image?.url}
                  defaultImgType="default_person"
                  fit="cover"
                />
                {isBestRD && (
                  <NewFlag
                    size="sm"
                    color="color_chip.blue.500"
                    textColor="color_chip.white"
                    content="Best RD"
                    icon={{
                      variant: 'ic_premium',
                      align: 'left',
                    }}
                    variant="solid"
                    position="absolute"
                    bottom="0"
                    left="calc(50% - 36px)"
                  />
                )}
              </Box>
              <Box maxWidth={648}>
                <Box>
                  <Text
                    as="span"
                    variant="subTitle2"
                    color="color_chip.primary"
                  >
                    {data.name}
                  </Text>
                  <Text
                    as="span"
                    variant="subTitle2"
                    color="color_chip.primary"
                    ml="2px"
                  >
                    {data.duty}
                  </Text>
                </Box>
                <HStack>
                  <Text
                    variant="label_lg_r"
                    color="color_chip.gray.800"
                    mr="2px"
                  >
                    Rehaus Designer
                  </Text>
                  <Tooltip
                    accentColor="color_chip.yellow.500"
                    direction="bottom_left"
                    message="한샘 공식 대리점 소속으로 인증된 인테리어 전문가를 말합니다."
                    width="232px"
                    zIndex={HEADER_Z_INDEX - 1}
                  >
                    <Icongraphy
                      resize
                      size={20}
                      variant="ic_information"
                      style={{
                        cursor: 'pointer',
                      }}
                      {...TrackingService.tagGTM('expert_infomation_button')}
                    />
                  </Tooltip>
                </HStack>

                {!_.isEmpty(data.badges) && (
                  <HStack mt={12} space={8} flexWrap="wrap">
                    {data.badges?.map(({ name, upperCode }, idx) => (
                      <NewFlag
                        key={`expert_badge_${data.code}_${idx}`}
                        variant="solid"
                        textColor="color_chip.primary"
                        color={
                          upperCode === 'RD_BDG_DPT'
                            ? 'color_chip.blue.50'
                            : 'color_chip.yellow.50'
                        }
                        content={name}
                      />
                    ))}
                  </HStack>
                )}

                <Box mt={24}>
                  <HStack space={4}>
                    <Icongraphy
                      variant="ic_comment"
                      resize
                      size={20}
                      useFillColor
                      fillColor="#666666"
                    />
                    <Text variant="label_lg_r" color="color_chip.gray.700">
                      소개
                    </Text>
                  </HStack>
                  <Text variant="body1" color="color_chip.primary" mt="8px">
                    {data.introduction ||
                      `한샘 리하우스 디자이너 ${data.name} 입니다.`}
                  </Text>
                </Box>
              </Box>
            </HStack>
            <Box>
              {isBestRD && (
                <Box
                  borderRadius={4}
                  py={16}
                  px={32}
                  width={252}
                  border="1px solid"
                  borderColor="color_chip.gray.200"
                >
                  <Text variant="subTitle7" color="color_chip.primary" mb="4px">
                    Best RD란?
                  </Text>
                  <Text variant="body2" color="color_chip.gray.800">
                    고객 만족 평가와 매출 실적에서 우수함이 검증된 전문가
                    입니다.
                  </Text>
                </Box>
              )}

              <NewBasicButton
                emphasis="high"
                size="lg"
                width={252}
                mt={40}
                onClick={handleRequestConsult}
                {...TrackingService.tagGTM(
                  'expert_detail_consult_request_button',
                )}
              >
                상담신청
              </NewBasicButton>
            </Box>
          </HStack>

          {(!_.isEmpty(constructionCaseData) ||
            _.isEmpty(data.workAvailableAreas)) && (
            <Box
              width="100%"
              height="8px"
              backgroundColor="color_chip.gray.50"
              mt={48}
            ></Box>
          )}
        </Box>

        {!_.isEmpty(constructionCaseData) && (
          <HStack mt={80} mb={12} space="space-between">
            {/* TODO: ssr data로 변경 필요함 */}
            <Text variant="headline4">시공 콘텐츠</Text>
            <NewTextButton
              size="lg"
              color="color_chip.gray.700"
              icon={{ variant: 'ic_arrow_right', align: 'right' }}
              onClick={handleOpenConstructionCase}
              {...TrackingService.tagGTM(
                'expert_detail_construction_case_more_button',
              )}
            >
              더보기
            </NewTextButton>
          </HStack>
        )}

        <Box
          width="100%"
          position="relative"
          onMouseOver={() => setIsHover(true)}
          onMouseLeave={() => setIsHover(false)}
        >
          <Swiper
            spaceBetween={32}
            slidesPerView={3}
            autoHeight
            onSwiper={setSwiper}
          >
            {_.slice(constructionCaseData, 0, 40).map((item) => {
              return (
                <SwiperSlide
                  key={`construction-case-popup-item${item.contentsNo}`}
                >
                  <ExpertSlideItem
                    item={item}
                    initialScrap={_.some(scrapList, {
                      id: item.contentsNo,
                    })}
                    handleMutateScrapList={handleMutateScrapList}
                  />
                </SwiperSlide>
              );
            })}
          </Swiper>

          {isHover && constructionCaseData.length > 3 && (
            <>
              <MovePagination
                size="md"
                variant="prev"
                onClick={handleClickPrev}
                position="absolute"
                left={0}
                top="50%"
                zIndex={10}
                style={{ transform: 'translateY(-50%)' }}
                {...TrackingService.tagGTM(
                  'expert_detail_construction_case_prev_button',
                )}
              />
              <MovePagination
                size="md"
                variant="next"
                onClick={handleClickNext}
                position="absolute"
                right={0}
                top="50%"
                zIndex={10}
                style={{ transform: 'translateY(-50%)' }}
                {...TrackingService.tagGTM(
                  'expert_detail_construction_case_next_button',
                )}
              />
            </>
          )}
        </Box>
      </ResponsiveBox>

      {!_.isEmpty(data.workAvailableAreas) && (
        <Box
          py={48}
          mt={80}
          backgroundColor="color_chip.blue.50"
          position="relative"
          zIndex={1}
        >
          <ResponsiveBox>
            <Text variant="headline4" color="color_chip.primary">
              상담 가능 지역
            </Text>
            <Box>
              <HStack mt={20} style={{ flexWrap: 'wrap', gap: '12px' }}>
                {data.workAvailableAreas?.slice(0, 20).map((item, idx) => {
                  return (
                    <Box
                      p={12}
                      borderRadius={2}
                      border="1px solid"
                      borderColor="color_chip.blue.100"
                      backgroundColor="color_chip.white"
                      key={`workable_area_${idx}`}
                    >
                      <Text variant="label_lg_m" color="color_chip.primary">
                        {item}
                      </Text>
                    </Box>
                  );
                })}
              </HStack>
            </Box>
          </ResponsiveBox>
        </Box>
      )}

      <ResponsiveBox>
        {data.customerReview?.content && (
          <VStack mt={80} px={20}>
            <DoubleQuotesStart width={28} height={28} />
            <Text variant="headline4" color="color_chip.primary" mt={20}>
              고객 만족 후기
            </Text>
            <Text
              variant="body1"
              color="color_chip.primary"
              textAlign="center"
              mt={36}
              maxWidth={1000}
            >
              {data.customerReview.content}
            </Text>
            <Text
              variant="label_md_m"
              color="color_chip.gray.700"
              mt={28}
              pb="8px"
            >
              {`${data.customerReview.address} ${data.customerReview.reviewer} 고객님`}
            </Text>
          </VStack>
        )}

        <Box mt={80}>
          <Box
            pb={12}
            borderBottom="1px solid"
            borderBottomColor="color_chip.gray.200"
          >
            <Text variant="headline4" color="color_chip.primary">
              기타 정보
            </Text>
          </Box>

          <HStack space={160} alignment="start" mt={36}>
            <HStack space={8} alignment="start" flex={1}>
              <HStack space={4} width={100}>
                <Icongraphy
                  variant="ic_check_done"
                  resize
                  size={16}
                  useFillColor
                  color="color_chip.gray.700"
                />
                <Text variant="label_md_m" color="color_chip.gray.800">
                  자격 인증
                </Text>
              </HStack>

              <Box>
                {!_.isEmpty(data.certifications) ? (
                  data.certifications?.map((item, idx) => (
                    <Text
                      key={`qualification_certification_${idx}`}
                      variant="label_smd_r"
                      color="color_chip.primary"
                      mt={idx !== 0 ? 12 : 0}
                    >
                      {item}
                    </Text>
                  ))
                ) : (
                  <Text variant="label_smd_r" color="color_chip.primary">
                    -
                  </Text>
                )}
              </Box>
            </HStack>

            <HStack space={8} alignment="start" flex={1}>
              <HStack space={4} width={100}>
                <Icongraphy
                  variant="ic_paper"
                  resize
                  size={16}
                  useFillColor
                  color="color_chip.gray.700"
                />
                <Text variant="label_md_m" color="color_chip.gray.800">
                  교육 수료
                </Text>
              </HStack>

              <Box flex={1}>
                {!_.isEmpty(data.courseCompletions) ? (
                  data.courseCompletions?.map((item, idx) => (
                    <Text
                      key={`education_certificate_${idx}`}
                      variant="label_smd_r"
                      color="color_chip.primary"
                      mt={idx !== 0 ? 12 : 0}
                    >
                      {item}
                    </Text>
                  ))
                ) : (
                  <Text variant="label_smd_r" color="color_chip.primary">
                    -
                  </Text>
                )}
              </Box>
            </HStack>
          </HStack>
        </Box>
      </ResponsiveBox>
      {!_.isEmpty(constructionCaseData) && (
        <BasicPopup
          handleClose={handleCloseConstructionCase}
          size="xlg"
          title="시공 콘텐츠"
          isOpen={isOpenConstructionCase}
        >
          <ExpertConstructionCase
            expertId={data.code}
            handleMutateScrapList={handleMutateScrapList}
            scrapList={scrapList}
          />
        </BasicPopup>
      )}
    </Box>
  );
};

export default ExpertProfile;
